import { router, type Href } from 'expo-router';
import { useEffect } from 'react';

import { Sentry } from '@/util/sentry';

/**
 * A layout gate's redirect, fired once per destination.
 *
 * Use this instead of `expo-router`'s `Redirect` in any layout that returns a redirect in
 * place of its `Stack`. That component hands an unmemoized callback to `useFocusEffect`,
 * whose dependency array includes the callback, so its `replace` runs again on every
 * render rather than once. Each `replace` is a navigation state update that renders the
 * layout again, and the values a gate decides on — `usePathname`, `useSegments` — trail
 * the update in flight, so the gate keeps deciding to redirect. The cascade outruns the
 * navigation settling and React stops it at the nested-update ceiling, taking the screen
 * down with "Maximum update depth exceeded".
 *
 * A leaf route that renders a redirect once and unmounts does not have this shape, which
 * is why the lint rule naming this component is scoped to layouts.
 *
 * The href is a string so the effect's dependency is compared by value, and the router is
 * the imperative singleton rather than `useRouter()`. Both are the same point: an object
 * href, or a hook that hands back a fresh object, is a new dependency every render and
 * rebuilds the loop.
 */
export function GateRedirect({ href }: { href: string }) {
  useEffect(() => {
    /*
     * A gate that cannot navigate leaves the user on a screen rendering nothing, with no
     * way to ask for anything else. That is worth reporting rather than throwing out of
     * an effect, which would replace a blank screen with an error boundary and still not
     * say why.
     */
    try {
      router.replace(href as Href);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'GateRedirect', source: 'layout-gate' },
        extra: { href, errorMessage: (error as Error)?.message },
      });
    }
  }, [href]);

  return null;
}
